import {useState, useCallback, type MouseEvent, type PropsWithChildren, type ReactNode} from 'react'
import {ChevronDownIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import styles from './ForYouItem.module.css'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

interface ForYouItemBaseProps {
  /** Leading visual displayed before the title - prefer using ForYouLeadingIcon */
  leadingVisual: ReactNode
  /** Whether the leading visual uses the larger status treatment */
  emphasizedLeadingVisual?: boolean
  /** Name of the item for analytics purposes */
  itemName: string
  /** Whether the item starts expanded (default: false) */
  defaultOpen?: boolean
  /** Optional slot displayed on the trailing side of the header */
  actionSlot?: ReactNode
  /** When true, the actionSlot is visually hidden until the item is hovered or focused (inbox-style) */
  revealActionOnHover?: boolean
  /** Optional description rendered below the title row */
  description?: ReactNode
  /** Optional padding for the item */
  padding?: 'default' | 'none'
  /** Visual treatment for items placed inside a separately framed container */
  variant?: 'default' | 'embedded'
  /** Apply aria-busy attribute */
  busy?: boolean
  /** Optional content rendered below the header row regardless of expansion state. Does not make the item expandable. */
  footer?: ReactNode
}
interface ForYouItemStringTitleProps extends ForYouItemBaseProps {
  /** Heading displayed in the header, rendered with the title style */
  title: string
  /** Accessible label for the toggle button. Optional for string titles — defaults to the title */
  ariaLabel?: string
}

interface ForYouItemNodeTitleProps extends ForYouItemBaseProps {
  /** Heading displayed in the header, rendered with the title style */
  title: ReactNode
  /** Accessible label for the toggle button. Required when the title is a ReactNode */
  ariaLabel: string
}

export type ForYouItemProps = ForYouItemStringTitleProps | ForYouItemNodeTitleProps

export function ForYouItem({
  leadingVisual,
  emphasizedLeadingVisual = false,
  title,
  ariaLabel,
  itemName,
  actionSlot,
  revealActionOnHover = false,
  description,
  children,
  defaultOpen = false,
  padding = 'default',
  variant = 'default',
  busy,
  footer,
}: PropsWithChildren<ForYouItemProps>) {
  const isExpandable = !!children
  const [isExpanded, setIsExpanded] = useState(defaultOpen)
  if (isExpanded && !isExpandable) setIsExpanded(false)

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const toggle = useCallback(() => {
    const next = !isExpanded
    sendPullRequestAnalyticsEvent('overview.for_you_item_toggle', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: itemName,
      is_open: next,
    })
    setIsExpanded(next)
  }, [isExpanded, sendPullRequestAnalyticsEvent, itemName])

  const handleRowClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target instanceof Element && e.target.closest('a, button')) return
      toggle()
    },
    [toggle],
  )

  const labelText = ariaLabel ?? (typeof title === 'string' ? title : '')
  const toggleAriaLabel = `${isExpanded ? 'Collapse' : 'Expand'} ${labelText}`.trim()

  return (
    <li className={clsx(styles.container, variant === 'embedded' && styles.embedded)} aria-busy={busy}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the chevron toggle button provides the keyboard-accessible affordance */}
      <div
        className={clsx(
          styles.headerRow,
          isExpanded ? styles.expanded : styles.collapsed,
          isExpandable && styles.expandable,
          revealActionOnHover && styles.revealActionOnHover,
          variant === 'embedded' && styles.embedded,
          emphasizedLeadingVisual && styles.emphasizedLeadingVisual,
        )}
        onClick={isExpandable ? handleRowClick : undefined}
      >
        <div className={styles.leadingVisual}>{leadingVisual}</div>

        <div className={styles.title}>{title}</div>

        {description && !isExpanded ? <div className={styles.description}>{description}</div> : null}

        <div className={styles.trailing}>
          {actionSlot}
          {isExpandable && (
            <IconButton
              icon={ChevronDownIcon}
              aria-label={toggleAriaLabel}
              // Override tooltip for readability
              description={isExpanded ? 'Collapse item' : 'Expand item'}
              aria-expanded={isExpanded}
              onClick={toggle}
              size="small"
              variant="invisible"
              className={clsx(styles.chevronButton, !isExpanded && styles.collapsed)}
            />
          )}
        </div>
      </div>
      {isExpanded && <div className={clsx(styles.body, padding === 'default' && styles.padded)}>{children}</div>}
      {footer}
    </li>
  )
}
